import * as React from "react"
import {Divider, Header, Message} from "semantic-ui-react"
import FormField, {
    Blank,
    IDropdownReadOnlyData,
} from "../form-field-generator"
import Loading from "../com_loader"
import Tree from "../tree copy"
import {
    DropdownOptionRawValue,
    FormFieldTypes,
    IDropdownOption,
    IFieldReducers,
    IFieldState,
} from "../../product-form/_reducers/_utils/product-form-types"
import {ITreeNode, ITreeState} from "../../../utils/tree/interfaces"
import {
    IStandardFields,
    ITechFields,
} from "../../product-form/_reducers/form-id-state"
import {
    createOldValue,
    FieldType,
    IOldValue,
    ISupplierDeclinedReference,
    ISupplierInfo,
    ISupplierReference,
    ITranslations,
    LockStatus,
    permanentFormIds,
} from "../../../models"
import ICustoms from "../../../models/product/customs"
import ITerritoi from "../../../models/product/territoi"
import IVat from "../../../models/product/vat"
import {ITax, ITechRawData} from "../../../models/bo"
import {Languages, get_Not_DeclinableFromTechfields} from "../../../utils"
import {StateFromReducersMapObject} from "../../../utils/combine-reducer"
import TaxesField from "../form-field-custom/com_ff_taxes"
import {deleteDoublonComplex} from "../../../utils/doublon-tools"
import {Multilang} from "../../../models/product/multilang-string";

interface IHandleChangeProps {
    code: string
    fieldValue?: any
    lock: LockStatus
    fieldType: FieldType
    overrideDefaultValue?: boolean
}

interface ICommonFieldsProps {
    supplier: ISupplierInfo
    techPropNodes: ITreeNode[]
    comPropNodes: ITreeNode[]
    lockable: boolean
    onFieldLockClick: (event: any, fieldType: FieldType) => void
    handleChangeField: ({
                            code,
                            fieldType,
                            lock,
                            fieldValue,
                            overrideDefaultValue,
                        }: IHandleChangeProps) => void
    formId: string
    standardFields: IStandardFields
    techFieldsIsLoading: boolean
    techNodeDisabledMessage: React.ReactNode
    comNodeDisabledMessage: React.ReactNode
    isVariant: boolean
    isMarketLeader: boolean
    techFields: ITechFields
    techRawData: ITechRawData[]
    tabIndex: number
    translations: ITranslations["productForm"]
    handleFieldModifyBtnClick?: (event: any, fieldType: FieldType) => void
    activeLang: Languages
    vatOptionList: IVat[]
    warrantyOptionList: IDropdownOption[]
    taxesList: ITax[]
    territoiOptionList: ITerritoi[]
    brandOptionList: string[]
    customsOptionList: ICustoms[]
    onFieldLooseFocus: () => void
    previousVersion?: ISupplierReference
    selectedPreviousVersion?: string | null
    // [x: string]: any
}

const CommonFields = ({
                          supplier,
                          formId,
                          lockable,
                          techPropNodes,
                          comPropNodes,
                          tabIndex,
                          translations,
                          handleChangeField,
                          onFieldLockClick,
                          handleFieldModifyBtnClick,
                          techFieldsIsLoading,
                          techFields: techFieldList,
                          techRawData,
                          activeLang,
                          techNodeDisabledMessage,
                          comNodeDisabledMessage,
                          isVariant,
                          isMarketLeader,
                          vatOptionList,
                          warrantyOptionList,
                          taxesList,
                          customsOptionList,
                          territoiOptionList,
                          brandOptionList,
                          standardFields,
                          onFieldLooseFocus,
                          previousVersion,
                          selectedPreviousVersion = null,
                      }: ICommonFieldsProps) => {
    const {
        label,
        price,
        activated,
        purchaseLabel,
        techTreeState,
        comTreeState,
        buyingReasons,
        comNode,
        techNode,
        minOrder,
        vatRate,
        supplierWarranty,
        bruneauWarranty,
        selectedTaxes,
        taxValues,
        customsCode,
        originCountry,
        brand,
        isTopRef,
    } = standardFields

    const isNewProduct = formId === permanentFormIds.NEW_PRODUCT

    const selectedTaxOptions = React.useMemo(
        () =>
            deleteDoublonComplex(
                taxesList?.map((tax) => ({
                    key: tax.taxCode,
                    text: tax.taxCode,
                    value: tax.taxCode,
                })) || [],
                "value"
            ),
        [taxesList]
    )

    return (
        <div style={{height: "100%", width: "100%"}}>
            <section className="text-field-div">
                <FormField
                    isNewProduct={isNewProduct}
                    lang={activeLang}
                    formId={formId}
                    lockable={lockable}
                    tabIndex={tabIndex++}
                    label={translations[label.label + "FieldLabel"]}
                    onChange={handleChangeField}
                    data={label}
                    oldValue={createOldValue(
                        selectedPreviousVersion,
                        previousVersion?.label,
                        previousVersion
                    )}
                    onFieldLooseFocus={onFieldLooseFocus}
                    onLockClick={onFieldLockClick}
                    onModifyClick={handleFieldModifyBtnClick}
                    style={{
                        lockDiv: {maxWidth: 984, width: "100%"},
                        textField: {maxWidth: "initial", width: "calc( 100% - 2em)"},
                    }}
                    multilangProps={{size: "small"}}
                />
                <FormField
                    isNewProduct={isNewProduct}
                    lang={activeLang}
                    formId={formId}
                    lockable={lockable}
                    tabIndex={tabIndex++}
                    currency={"euro"}
                    label={translations[price.label + "FieldLabel"]}
                    onChange={handleChangeField}
                    data={price}
                    oldValue={createOldValue(
                        selectedPreviousVersion,
                        previousVersion?.price,
                        previousVersion
                    )}
                    onFieldLooseFocus={onFieldLooseFocus}
                    onLockClick={onFieldLockClick}
                    onModifyClick={handleFieldModifyBtnClick}
                    numberProps={{min: 0, step: 0.01}}
                />
                {!isVariant && (
                    <FormField
                        isNewProduct={isNewProduct}
                        lang={activeLang}
                        formId={formId}
                        lockable={lockable}
                        tabIndex={tabIndex++}
                        label={translations[brand.label + "FieldLabel"]}
                        onChange={handleChangeField}
                        data={brand}
                        oldValue={createOldValue(
                            selectedPreviousVersion,
                            previousVersion?.brand,
                            previousVersion
                        )}
                        onFieldLooseFocus={onFieldLooseFocus}
                        onLockClick={onFieldLockClick}
                        onModifyClick={handleFieldModifyBtnClick}
                        dropdownProps={{
                            // @ts-ignore
                            options: brandOptionList,
                            customPlaceholderOption: [
                                {
                                    key: "-",
                                    text: "-",
                                    value: "-1",
                                },
                                {
                                    key: 0,
                                    text: supplier.supplierName,
                                    value: supplier.supplierName,
                                },
                            ],
                            isObject: false,
                            lazyload: true,
                            style: {
                                div: {maxWidth: 300, width: 300},
                                input: {width: "100%"},
                            },
                        }}
                    />
                )}
                {/* <FormField type={"placeholder"} triple /> */}
                {!isVariant && (
                    <FormField
                        isNewProduct={isNewProduct}
                        lang={activeLang}
                        formId={formId}
                        lockable={lockable}
                        tabIndex={tabIndex++}
                        label={translations[minOrder.label + "FieldLabel"]}
                        onChange={handleChangeField}
                        data={minOrder}
                        oldValue={createOldValue(
                            selectedPreviousVersion,
                            previousVersion?.minOrder,
                            previousVersion
                        )}
                        onFieldLooseFocus={onFieldLooseFocus}
                        onLockClick={onFieldLockClick}
                        onModifyClick={handleFieldModifyBtnClick}
                        numberProps={{min: 0, step: 1}}
                    />
                )}
                {!isVariant && (
                    <FormField
                        isNewProduct={isNewProduct}
                        lang={activeLang}
                        formId={formId}
                        lockable={lockable}
                        tabIndex={tabIndex++}
                        label={translations[vatRate.label + "FieldLabel"]}
                        onChange={handleChangeField}
                        data={vatRate}
                        oldValue={createOldValue(
                            selectedPreviousVersion,
                            previousVersion?.vatRate,
                            previousVersion
                        )}
                        onFieldLooseFocus={onFieldLooseFocus}
                        onLockClick={onFieldLockClick}
                        onModifyClick={handleFieldModifyBtnClick}
                        dropdownProps={{
                            // @ts-ignore
                            options: vatOptionList,
                            icon: "percent",
                            minCaracters: 0,
                            isObject: true,
                        }}
                    />
                )}
                {!isVariant && (
                    <FormField
                        isNewProduct={isNewProduct}
                        lang={activeLang}
                        formId={formId}
                        lockable={lockable}
                        tabIndex={tabIndex++}
                        label={translations[originCountry.label + "FieldLabel"]}
                        onChange={handleChangeField}
                        data={originCountry}
                        oldValue={createOldValue(
                            selectedPreviousVersion,
                            previousVersion?.originCountry,
                            previousVersion
                        )}
                        onFieldLooseFocus={onFieldLooseFocus}
                        onLockClick={onFieldLockClick}
                        onModifyClick={handleFieldModifyBtnClick}
                        // @ts-ignore
                        dropdownProps={{options: territoiOptionList, isObject: true}}
                    />
                )}
                {!isVariant && (
                    <FormField
                        isNewProduct={isNewProduct}
                        lang={activeLang}
                        formId={formId}
                        lockable={lockable}
                        tabIndex={tabIndex++}
                        label={translations[customsCode.label + "FieldLabel"]}
                        onChange={handleChangeField}
                        data={customsCode}
                        oldValue={createOldValue(
                            selectedPreviousVersion,
                            previousVersion?.customsCode,
                            previousVersion
                        )}
                        onFieldLooseFocus={onFieldLooseFocus}
                        onLockClick={onFieldLockClick}
                        onModifyClick={handleFieldModifyBtnClick}
                        searchProps={{options: customsOptionList}}
                    />
                )}
                <FormField
                    isNewProduct={isNewProduct}
                    lang={activeLang}
                    formId={formId}
                    lockable={lockable}
                    tabIndex={tabIndex++}
                    label={translations[activated.label + "FieldLabel"]}
                    onChange={handleChangeField}
                    data={isTopRef.value ? isTopRef : activated}
                    oldValue={createOldValue(
                        selectedPreviousVersion,
                        isTopRef.value ? isTopRef.value : activated.value,
                        previousVersion
                    )}
                    onFieldLooseFocus={onFieldLooseFocus}
                    onLockClick={onFieldLockClick}
                    onModifyClick={handleFieldModifyBtnClick}
                    disabled={isTopRef.value ? true : false}
                    checkboxProps={{checkboxStyle: "toggle"}}
                />
                {!isVariant && (
                    <FormField
                        isNewProduct={isNewProduct}
                        lang={activeLang}
                        formId={formId}
                        lockable={lockable}
                        tabIndex={tabIndex++}
                        label={translations[supplierWarranty.label + "FieldLabel"]}
                        onChange={handleChangeField}
                        data={supplierWarranty}
                        oldValue={createOldValue(
                            selectedPreviousVersion,
                            previousVersion?.supplierWarranty,
                            previousVersion
                        )}
                        onFieldLooseFocus={onFieldLooseFocus}
                        onLockClick={onFieldLockClick}
                        onModifyClick={handleFieldModifyBtnClick}
                    />
                )}
                {!isVariant && isMarketLeader && (
                    <FormField
                        isNewProduct={isNewProduct}
                        lang={activeLang}
                        formId={formId}
                        lockable={lockable}
                        tabIndex={tabIndex++}
                        label={translations[bruneauWarranty.label + "FieldLabel"]}
                        onChange={handleChangeField}
                        data={bruneauWarranty}
                        oldValue={createOldValue(
                            selectedPreviousVersion,
                            previousVersion?.bruneauWarranty || undefined,
                            previousVersion
                        )}
                        onFieldLooseFocus={onFieldLooseFocus}
                        onLockClick={onFieldLockClick}
                        onModifyClick={handleFieldModifyBtnClick}
                        dropdownProps={{
                            // @ts-ignore
                            options: warrantyOptionList,
                            labelClassName: "market-leader",
                            // icon: "percent",
                            minCaracters: 0,
                        }}
                    />
                )}
                {!isVariant && [
                    <FormField
                        key={"selectedTaxes"}
                        isNewProduct={isNewProduct}
                        lang={activeLang}
                        formId={formId}
                        lockable={lockable}
                        tabIndex={tabIndex++}
                        label={translations[selectedTaxes.label + "FieldLabel"]}
                        onChange={handleChangeField}
                        data={selectedTaxes}
                        oldValue={createOldValue(
                            selectedPreviousVersion,
                            previousVersion?.selectedTaxes,
                            previousVersion
                        )}
                        onFieldLooseFocus={onFieldLooseFocus}
                        onLockClick={onFieldLockClick}
                        onModifyClick={handleFieldModifyBtnClick}
                        dropdownProps={{
                            className: "selected-taxes",
                            options: selectedTaxOptions,
                            // icon: "percent",
                            minCaracters: 0,
                            isObject: false,
                            multiple: true,
                            style: {
                                globalDiv: {maxWidth: 984},
                                div: {
                                    maxWidth: 956,
                                    width: "calc(100% - 2em)",
                                    minContent: 256,
                                },
                                // input: { maxWidth: 956, width: "100%" },
                            },
                        }}
                    />,
                    <TaxesField
                        key={"taxValues"}
                        taxesList={taxesList}
                        selectedTaxes={selectedTaxes}
                        onChange={handleChangeField}
                        data={taxValues}
                        formId={formId}
                        oldValueDate={selectedPreviousVersion}
                        oldValue={previousVersion?.taxValues}
                    />,
                ]}
            </section>
            <section className="client-visible-infos">
                <Message>
                    <Message.Header>{translations.argumentaryZoneLabel}</Message.Header>
                    <div style={{color: "black"}}>
                        <FormField
                            isNewProduct={isNewProduct}
                            lang={activeLang}
                            formId={formId}
                            lockable={lockable}
                            tabIndex={tabIndex++}
                            label={
                                isTopRef.value
                                    ? translations.headPurchaseLabel
                                    : translations.productPurchaseLabel
                            }
                            onChange={handleChangeField}
                            data={purchaseLabel}
                            oldValue={createOldValue(
                                selectedPreviousVersion,
                                previousVersion?.purchaseLabel,
                                previousVersion
                            )}
                            onFieldLooseFocus={onFieldLooseFocus}
                            onLockClick={onFieldLockClick}
                            onModifyClick={handleFieldModifyBtnClick}
                            // textareaProps={{ rows: 1 }}
                            multilangProps={{multiLine: false}}
                        />
                        <FormField
                            isNewProduct={isNewProduct}
                            lang={activeLang}
                            formId={formId}
                            lockable={lockable}
                            tabIndex={tabIndex++}
                            label={translations[buyingReasons.label + "FieldLabel"]}
                            onChange={handleChangeField}
                            data={buyingReasons}
                            oldValue={createOldValue(
                                selectedPreviousVersion,
                                previousVersion?.buyingReasons,
                                previousVersion
                            )}
                            onFieldLooseFocus={onFieldLooseFocus}
                            onLockClick={onFieldLockClick}
                            onModifyClick={handleFieldModifyBtnClick}
                            // textareaProps={{ rows: 10 }}
                        />
                    </div>
                </Message>
            </section>
            {/* ------------------- TECH FIELDS ------------------------- */}
            {!isVariant && (
                <>
                    <Divider style={{width: "100%"}}/>
                    <h2 className="form-title"> {translations.treeTitle} </h2>
                    <div className="tree-buttons">
                        <Tree
                            isNewProduct={isNewProduct}
                            lockable={lockable}
                            // disabled={!isNewProduct && isVariant}
                            disabledMessage={comNodeDisabledMessage}
                            tabIndex={tabIndex++}
                            title={translations.comTreeLabel}
                            type="com"
                            propNodes={comPropNodes}
                            selected={comNode}
                            previousSelected={createOldValue(
                                selectedPreviousVersion,
                                previousVersion?.comNode,
                                previousVersion
                            )}
                            onChange={handleChangeField}
                            treeState={comTreeState}
                            nodesLoadingState={false}
                            translations={translations}
                            onLockClick={onFieldLockClick}
                            onModifyClick={handleFieldModifyBtnClick}
                        />
                        {!isTopRef.value && (
                            <Tree
                                isNewProduct={isNewProduct}
                                lockable={lockable}
                                // disabled={!isNewProduct && isVariant}
                                disabledMessage={techNodeDisabledMessage}
                                tabIndex={tabIndex++}
                                title={translations.techTreeLabel}
                                type="tech"
                                propNodes={techPropNodes}
                                selected={techNode}
                                previousSelected={createOldValue(
                                    selectedPreviousVersion,
                                    previousVersion?.techNode,
                                    previousVersion
                                )}
                                onChange={handleChangeField}
                                treeState={techTreeState}
                                nodesLoadingState={false}
                                translations={translations}
                                onLockClick={onFieldLockClick}
                                onModifyClick={handleFieldModifyBtnClick}
                            />
                        )}
                    </div>
                </>
            )}
            <Divider style={{width: "100%"}}/>
            <TechFieldsComponent
                techFields={
                    isTopRef.value
                        ? get_Not_DeclinableFromTechfields(techFieldList)
                        : techFieldList
                }
                techRawData={techRawData}
                previousVersion={previousVersion}
                isNewProduct={isNewProduct}
                selectedPreviousVersion={selectedPreviousVersion}
                onFieldLockClick={onFieldLockClick}
                formId={formId}
                activeLang={activeLang}
                tabIndex={tabIndex}
                techFieldsIsLoading={techFieldsIsLoading}
                lockable={lockable}
                handleChangeField={handleChangeField}
                onFieldLooseFocus={onFieldLooseFocus}
                handleFieldModifyBtnClick={handleFieldModifyBtnClick}
                translations={translations}
            />
        </div>
    )
}

export default CommonFields

/// TECHFIELDS COMPONENT
interface IDeclinationValues {
    [x: string]: DropdownOptionRawValue[]
}

interface ITechfieldsComponentProps {
    onlyFields?: boolean
    techFields: ITechFields
    techRawData: ITechRawData[]
    isNewProduct: boolean
    previousVersion?: ISupplierReference
    selectedPreviousVersion?: string | null
    formId: string
    activeLang: Languages
    tabIndex: number
    valCartechs?: IDeclinationValues
    techFieldsIsLoading: boolean
    lockable: boolean
    translations: ITranslations["productForm"]
    allowMultipleSelection?: boolean
    allowAddOptions?: boolean
    allDeclinableAreDropdown?: boolean
    goToProduct?: (droopyId: string, declinedDroopyId?: string) => void
    handleFieldModifyBtnClick?: (event: any, fieldType: FieldType) => void
    onFieldLockClick: (event: any, fieldType: FieldType) => void
    onFieldLooseFocus: () => void
    handleChangeField: ({
                            code,
                            fieldType,
                            lock,
                            fieldValue,
                            overrideDefaultValue,
                        }: IHandleChangeProps) => void
}

export const TechFieldsComponent = ({
                                        onlyFields = false,
                                        allowAddOptions = false,
                                        allowMultipleSelection = false,
                                        allDeclinableAreDropdown = false,
                                        techFields,
                                        goToProduct,
                                        techRawData,
                                        previousVersion,
                                        selectedPreviousVersion = null,
                                        onFieldLockClick,
                                        formId,
                                        isNewProduct,
                                        activeLang,
                                        valCartechs,
                                        tabIndex,
                                        techFieldsIsLoading,
                                        lockable,
                                        handleChangeField,
                                        onFieldLooseFocus,
                                        handleFieldModifyBtnClick,
                                        translations,
                                    }: ITechfieldsComponentProps) => {
    const techFieldsValues = Object.values(techFields) as Array<IFieldState<any>>

    const categories = {"": []} as {
        [category: string]: Array<IFieldState<any>>
    }

    techRawData.forEach((c) => {
        const field = techFieldsValues.find((t) => t.code === c.codeEnum)
        if (!field) {
            return
        } else {
            categories[c.category] = [...(categories[c.category] || []), field]
        }
    })

    var techFieldsComponents = Object.keys(categories).reduce(
        (acc, key) => {
            acc[key] = categories[key]
                .sort((a: IFieldState<any>, b: IFieldState<any>) =>
                    a.label === "" ? 1 : a.label > b.label ? 1 : -1
                )
                .filter((field: IFieldState<any>) => field.label !== "")
                .map((field: IFieldState<any>) => {
                    const previousValue = previousVersion?.techFields?.find(
                        (f) => f.codeEnum === field.code
                    )
                    const data: typeof field = allDeclinableAreDropdown
                        ? {...field, formFieldType: FormFieldTypes.Dropdown}
                        : field

                    let readOnlyData = undefined
                    if (valCartechs && valCartechs[data.code]) {
                        readOnlyData = valCartechs[data.code]
                            .map(
                                (x) =>
                                    ({
                                        value: x,
                                        // label: x,
                                        className: "hidden-cross declined",
                                        onClick: () => {
                                            return
                                        }, //goToProduct?.(formId, x.source.droopyId),
                                    } as IDropdownReadOnlyData)
                            )
                        if (!Array.isArray(data.value)) {
                            if (data.value === "-1" || data.value === "") {
                                data.value = []
                            } else {
                                data.value = [data.value]
                            }
                        }
                    }

                    const dropdownProps = {
                        options: field.options || [],
                        multiple: allowMultipleSelection,
                        allowAdditionsIfNoOptions: allowAddOptions,
                        readOnlyData,
                        style: allDeclinableAreDropdown
                            ? {
                                // globalDiv: {
                                //   flex: 5,
                                // },
                                div: {
                                    // width: "97%",
                                    maxWidth: "600px",
                                    width: "initial",
                                    // minHeight: 42,
                                    // marginLeft: "1em",
                                    // marginTop: 0,
                                },
                                input: {flexWrap: "wrap"},
                                label: {minWidth: "max-content"},
                            }
                            : {},
                    }

                    return (
                        <FormField
                            isNewProduct={isNewProduct}
                            lang={activeLang}
                            formId={formId}
                            lockable={lockable}
                            tabIndex={tabIndex++}
                            data={data}
                            oldValue={createOldValue(
                                selectedPreviousVersion,
                                previousValue?.valueCode || previousValue?.value,
                                previousVersion
                            )}
                            label={field.label}
                            key={field.code}
                            onChange={handleChangeField}
                            onFieldLooseFocus={onFieldLooseFocus}
                            onLockClick={onFieldLockClick}
                            onModifyClick={handleFieldModifyBtnClick}
                            dropdownProps={dropdownProps}
                        />
                    )
                })
            return acc
        },
        {} as {
            [category: string]: JSX.Element[]
        }
    )

    const title =
        Object.keys(techFields).length > 0
            ? translations.normalTechFieldsTitle
            : translations.emptyTechFieldsTitle

    const loader = techFieldsIsLoading ? (
        <Loading message={translations.loadingTechFields}/>
    ) : (
        ""
    )

    if (onlyFields) {
        return (
            <>
                {Object.keys(techFieldsComponents)
                    .sort((a, b) => (!a ? 1 : !b ? -1 : a > b ? 1 : -1))
                    .map((key) => techFieldsComponents[key])}
            </>
        )
    }

    return (
        <section className="tech-fields">
            {loader}
            <h2 className="form-title">{title}</h2>
            <div className="text-field-div tech">
                {Object.keys(techFieldsComponents)
                    .sort((a, b) => (!a ? 1 : !b ? -1 : a > b ? 1 : -1))
                    .map((key) => (
                        <>
                            <Header>{key || "Sans catégorie"}</Header>
                            {techFieldsComponents[key]}
                            <Divider/>
                        </>
                    ))}
            </div>
        </section>
    )
}
